import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Wind, ShieldCheck, CalendarCheck, Wrench, Snowflake, Leaf, ArrowRight, Star, MapPin, HeartPulse, Zap, Droplets, Check, X, Clock, Sparkles } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { QuickBooking } from "@/components/QuickBooking";
import { NetworkMap } from "@/components/NetworkMap";
import { CheckList, StepList } from "@/components/Reveal";
import { TECH_URL } from "@/lib/api";

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

export default function Home() {
  const navigate = useNavigate();
  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:py-20">
          <motion.div {...fade}>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#E0F2FE] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#1D9BF0]">
              <Snowflake className="h-3.5 w-3.5" /> Particuliers & Professionnels
            </span>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-[#263871] sm:text-5xl lg:text-6xl">
              {["Pour", "ma", "clim,"].map((w, i) => (
                <motion.span key={i} className="mr-[0.22em] inline-block" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.12, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
                  {w}
                </motion.span>
              ))}
              <br />
              <motion.span className="slogan-ici inline-block text-[#1D9BF0]" initial={{ opacity: 0, scale: 0.6, y: 14 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 0.55, type: "spring", stiffness: 300, damping: 12 }}>
                c'est ici.
              </motion.span>
            </h1>
            <p className="mt-5 max-w-md text-lg text-slate-600">
              Entretien, nettoyage et installation de climatisation par des techniciens certifiés. Réservez en ligne en quelques clics, prix transparents.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button data-testid="hero-install-btn" onClick={() => navigate("/installation")} className="h-12 rounded-full bg-[#263871] px-8 text-base hover:bg-[#35508F]">
                Demander un devis d'installation <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button data-testid="hero-maintenance-btn" variant="outline" onClick={() => document.getElementById("entretien")?.scrollIntoView({ behavior: "smooth" })} className="h-12 rounded-full border-2 px-8 text-base">
                Entretenir ma clim
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> 4,9/5 clients</span>
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#1D9BF0]" /> Partout en France</span>
            </div>
          </motion.div>
          <motion.div {...fade} className="relative">
            <div data-testid="hero-install-card" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-blue-900/10 sm:p-7">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#E0F2FE] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#1D9BF0]">
                <Snowflake className="h-3.5 w-3.5" /> Installation de clim
              </span>
              <h3 className="mt-4 text-2xl font-bold text-[#263871]">Un projet d'installation ?</h3>
              <p className="mt-2 text-slate-600">Décrivez votre besoin en 2 minutes : un installateur certifié proche de chez vous vous recontacte pour un devis 100% gratuit.</p>
              <CheckList className="mt-5 space-y-2.5 text-sm" items={["Devis gratuit et sans engagement", "Installateurs certifiés et vérifiés", "Réponse rapide près de chez vous"]} />
              <Button data-testid="hero-install-card-btn" onClick={() => navigate("/installation")} className="mt-6 h-12 w-full rounded-full bg-[#1D9BF0] hover:bg-[#57A4FF]">
                Démarrer ma demande <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-8 sm:px-6 md:grid-cols-4 lg:px-8">
          {[
            { icon: ShieldCheck, t: "Techniciens vérifiés" },
            { icon: CalendarCheck, t: "Réservation en ligne" },
            { icon: Leaf, t: "Air sain & économies" },
            { icon: Wind, t: "Preuve avant/après" },
          ].map((i, k) => (
            <div key={k} className="flex items-center gap-3">
              <div className="rounded-xl bg-[#E0F2FE] p-2.5"><i.icon className="h-5 w-5 text-[#1D9BF0]" /></div>
              <span className="text-sm font-semibold text-slate-700">{i.t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ENTRETIEN - RESERVATION */}
      <section id="entretien" className="bg-[#F8FAFC] py-16">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <motion.div {...fade}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D9BF0]">Entretien & nettoyage</p>
            <h2 className="mt-2 text-3xl font-bold text-[#263871] sm:text-4xl">À la demande ou en abonnement</h2>
            <p className="mt-3 max-w-md text-slate-600">Réservez un nettoyage ponctuel ou souscrivez un forfait avec une visite tous les 3 mois. Prix transparents, photos avant/après systématiques.</p>
            <CheckList className="mt-6 space-y-3" items={["45 min par climatiseur, par un technicien certifié", "Désinfection complète + parfum de finition au choix", "Abonnement dégressif dès 3 climatiseurs"]} />
          </motion.div>
          <motion.div {...fade} className="relative">
            <QuickBooking />
          </motion.div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <motion.div {...fade} className="mb-10 max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D9BF0]">Nos services</p>
          <h2 className="mt-2 text-3xl font-bold text-[#263871] sm:text-4xl">Une plateforme, deux métiers</h2>
        </motion.div>
        <div className="grid gap-6 md:grid-cols-12">
          <motion.div {...fade} className="cl-gradient md:col-span-7 rounded-3xl p-8 text-white">
            <Wrench className="h-9 w-9" />
            <h3 className="mt-4 text-2xl font-bold">Entretien & nettoyage</h3>
            <p className="mt-2 max-w-md text-sky-100">Vos climatiseurs nettoyés et désinfectés par un intervenant certifié. Photos avant/après systématiques pour garantir la qualité.</p>
            <Button onClick={() => navigate("/services")} className="mt-6 rounded-full bg-white text-[#263871] hover:bg-sky-50">Entretenir ma clim</Button>
          </motion.div>
          <motion.div {...fade} className="md:col-span-5 rounded-3xl border border-slate-200 bg-white p-8">
            <div className="rounded-xl bg-[#E0F2FE] p-2.5 w-fit"><Snowflake className="h-7 w-7 text-[#1D9BF0]" /></div>
            <h3 className="mt-4 text-2xl font-bold text-[#263871]">Installation</h3>
            <p className="mt-2 text-slate-600">Besoin d'une nouvelle clim ? On vous met en relation avec un poseur qualifié près de chez vous.</p>
            <Button variant="outline" onClick={() => navigate("/installation")} className="mt-6 rounded-full border-2">Demander un devis</Button>
          </motion.div>
          <motion.div {...fade} className="md:col-span-5 overflow-hidden rounded-3xl">
            <img src={TECH_URL} alt="Technicien" className="h-full min-h-[220px] w-full object-cover" />
          </motion.div>
          <motion.div {...fade} className="md:col-span-7 rounded-3xl border border-slate-200 bg-white p-8">
            <h3 className="text-2xl font-bold text-[#263871]">Contrats d'entretien</h3>
            <p className="mt-2 text-slate-600">Souscrivez un forfait trimestriel : nous passons tous les 3 mois. Jusqu'à 25% d'économies d'énergie et une clim qui dure.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              {["Visite tous les 3 mois", "20€/mois/clim", "Dégressif dès 3 clims"].map((p) => (
                <span key={p} className="rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-slate-700">{p}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* AVANTAGES */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D9BF0]">Pourquoi entretenir</p>
          <h2 className="mt-2 text-3xl font-bold text-[#263871] sm:text-4xl">Les bénéfices d'une clim propre</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Zap, t: "Économies d'énergie", d: "Une clim encrassée surconsomme. Un nettoyage régulier réduit la facture jusqu'à 25%." },
              { icon: HeartPulse, t: "Santé respiratoire", d: "Élimination des allergènes, moisissures et mauvaises odeurs pour un air vraiment sain." },
              { icon: Droplets, t: "Moins de bactéries", d: "Désinfection complète de l'unité : on supprime les bactéries qui prolifèrent dans l'humidité." },
              { icon: ShieldCheck, t: "Durée de vie x2", d: "Un matériel entretenu tombe moins en panne et dure bien plus longtemps." },
            ].map((c) => (
              <motion.div key={c.t} {...fade} className="rounded-2xl border border-slate-200 p-6">
                <div className="w-fit rounded-xl bg-[#E0F2FE] p-2.5"><c.icon className="h-6 w-6 text-[#1D9BF0]" /></div>
                <h3 className="mt-4 font-bold text-[#263871]">{c.t}</h3>
                <p className="mt-2 text-sm text-slate-600">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARATIF DIY vs COMPLET */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D9BF0]">Entretien vs Nettoyage</p>
          <h2 className="mt-2 text-3xl font-bold text-[#263871] sm:text-4xl">Aspirer les filtres ne suffit pas</h2>
          <p className="mt-3 text-slate-600">Comprenez la différence entre l'entretien que vous faites vous-même et un nettoyage complet réalisé par un professionnel.</p>
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <motion.div {...fade} className="rounded-3xl border border-slate-200 bg-white p-8">
            <h3 className="text-xl font-bold text-[#263871]">Entretien soi-même</h3>
            <p className="text-sm text-slate-500">Aspirer / rincer les filtres</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex gap-2 text-slate-700"><Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />Gratuit et rapide, à faire tous les mois</li>
              <li className="flex gap-2 text-slate-700"><Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />Maintient un flux d'air correct</li>
              <li className="flex gap-2 text-slate-400"><X className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />Ne désinfecte pas l'échangeur ni la turbine</li>
              <li className="flex gap-2 text-slate-400"><X className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />N'élimine pas les bactéries et moisissures</li>
              <li className="flex gap-2 text-slate-400"><X className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />Les odeurs persistent</li>
            </ul>
          </motion.div>
          <motion.div {...fade} className="cl-gradient rounded-3xl p-8 text-white">
            <div className="flex items-center gap-2"><Sparkles className="h-5 w-5" /><h3 className="text-xl font-bold">Nettoyage complet Climateasy</h3></div>
            <p className="text-sm text-sky-200">Par un technicien certifié — dès 60€ HT</p>
            <CheckList className="mt-5 space-y-3 text-sm" itemClass="text-white" circleClass="bg-white/20 text-white" items={[
              "Démontage et nettoyage en profondeur",
              "Désinfection bactéricide de l'échangeur et de la turbine",
              "Suppression des odeurs + parfum de finition au choix",
              "Photos avant/après comme preuve de propreté",
              "Économies d'énergie et air sain garantis",
            ]} />
            <Button onClick={() => navigate("/inscription")} className="mt-6 rounded-full bg-white text-[#263871] hover:bg-sky-50">Réserver un nettoyage</Button>
          </motion.div>
        </div>
      </section>

      {/* ETAPES DES PRESTATIONS */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#263871] sm:text-4xl">Le déroulé de chaque prestation</h2>
          <div className="mt-10 grid gap-10 lg:grid-cols-2">
            <div>
              <h3 className="flex items-center gap-2 text-lg font-bold text-[#1D9BF0]"><Wrench className="h-5 w-5" />Nettoyage / Entretien</h3>
              <StepList badgeClass="bg-[#263871]" steps={[
                "Vous réservez en ligne en indiquant le nombre de clims (45 min/clim).",
                "Un technicien proche accepte la mission et se déplace.",
                "Photo avant, démontage, nettoyage complet et désinfection.",
                "Parfum de finition au choix, photo après et paiement sécurisé.",
              ]} />
            </div>
            <div>
              <h3 className="flex items-center gap-2 text-lg font-bold text-[#1D9BF0]"><Snowflake className="h-5 w-5" />Installation</h3>
              <StepList badgeClass="bg-[#1D9BF0]" steps={[
                "Vous décrivez votre besoin (nombre de clims, type de logement).",
                "Nous transmettons votre demande à un poseur certifié proche.",
                "Le poseur vous contacte et établit un devis gratuit.",
                "Pose réalisée, et possibilité d'ajouter un contrat d'entretien.",
              ]} />
            </div>
          </div>
        </div>
      </section>

      {/* RESEAU - CARTE DE FRANCE */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <motion.div {...fade} className="lg:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D9BF0]">Notre réseau</p>
            <h2 className="mt-2 text-3xl font-bold text-[#263871] sm:text-4xl">Des pros partout en France</h2>
            <p className="mt-3 text-slate-600">Un réseau de techniciens d'entretien et d'installateurs certifiés, proches de chez vous, prêts à intervenir rapidement.</p>
            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3"><span className="h-4 w-4 rounded-full border-2 border-white shadow" style={{ background: "#1D9BF0" }} /><span className="text-sm font-semibold text-slate-700">Techniciens d'entretien</span></div>
              <div className="flex items-center gap-3"><span className="h-4 w-4 rounded-full border-2 border-white shadow" style={{ background: "#F59E0B" }} /><span className="text-sm font-semibold text-slate-700">Installateurs de clim</span></div>
            </div>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-8">
            <NetworkMap />
          </motion.div>
        </div>
      </section>

      {/* FORFAITS TRIMESTRIELS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D9BF0]">Forfaits d'entretien</p>
          <h2 className="mt-2 text-3xl font-bold text-[#263871] sm:text-4xl">On passe tous les 3 mois</h2>
          <p className="mx-auto mt-3 max-w-xl text-slate-600">Un abonnement simple à <strong>20€/mois/clim</strong>, dégressif selon le nombre de climatiseurs. Visites planifiées automatiquement.</p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { n: "1 à 2 clims", price: "20€", sub: "/mois/clim", disc: null, accent: false },
            { n: "Dès 3 clims", price: "18€", sub: "/mois/clim", disc: "-10%", accent: true },
            { n: "Dès 6 clims", price: "17€", sub: "/mois/clim", disc: "-15%", accent: false },
          ].map((p) => (
            <motion.div key={p.n} {...fade} className={`relative flex flex-col rounded-3xl border p-8 ${p.accent ? "cl-gradient border-transparent text-white" : "border-slate-200 bg-white"}`}>
              {p.disc && <span className={`absolute right-5 top-5 rounded-full px-3 py-1 text-xs font-bold ${p.accent ? "bg-white text-[#263871]" : "bg-[#E0F2FE] text-[#1D9BF0]"}`}>{p.disc}</span>}
              <h3 className={`text-lg font-bold ${p.accent ? "text-white" : "text-[#263871]"}`}>{p.n}</h3>
              <p className="mt-4"><span className={`text-4xl font-extrabold ${p.accent ? "text-white" : "text-[#263871]"}`}>{p.price}</span><span className={p.accent ? "text-sky-200" : "text-slate-400"}>{p.sub}</span></p>
              <ul className={`mt-5 flex-1 space-y-2 text-sm ${p.accent ? "text-sky-100" : "text-slate-600"}`}>
                <li className="flex gap-2"><Clock className={`h-4 w-4 ${p.accent ? "text-sky-300" : "text-[#1D9BF0]"}`} />Visite tous les 3 mois</li>
                <li className="flex gap-2"><Check className={`h-4 w-4 ${p.accent ? "text-sky-300" : "text-emerald-500"}`} />Nettoyage complet à chaque passage</li>
                <li className="flex gap-2"><Check className={`h-4 w-4 ${p.accent ? "text-sky-300" : "text-emerald-500"}`} />Planification automatique</li>
              </ul>
              <Button onClick={() => navigate("/inscription")} className={`mt-6 w-full rounded-full ${p.accent ? "bg-white text-[#263871] hover:bg-sky-50" : "bg-[#263871] text-white hover:bg-[#35508F]"}`}>Souscrire</Button>
            </motion.div>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
